import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { brandPalette } from "../../../constants/brandPalette";
import {
  saveWhatsappTemplateFail,
  saveWhatsappTemplateRequest,
  whatsappTemplateDraftChange,
  whatsappTemplateResetDraft,
} from "../../../modules/admin-modules/whatsapp-templates/Actions";
import {
  buildSavePayload,
  extractPlaceholders,
  validateTemplateDraft,
} from "../../../modules/admin-modules/whatsapp-templates/utils";
import TemplatePreviewBubble from "./TemplatePreviewBubble";
import VariableChips from "./VariableChips";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
};

const fieldError = (validationErrors, key) => {
  const value = validationErrors?.[key];
  if (!value) return "";
  return Array.isArray(value) ? value[0] : String(value);
};

const EditTemplateForm = ({ onBack }) => {
  const dispatch = useDispatch();
  const draft = useSelector((state) => state.whatsappTemplates.selected);
  const dirty = useSelector((state) => state.whatsappTemplates.dirty);
  const saving = useSelector((state) => state.whatsappTemplates.saving);
  const validationErrors = useSelector(
    (state) => state.whatsappTemplates.validationErrors
  );

  const placeholders = useMemo(
    () => extractPlaceholders(draft?.body_content),
    [draft?.body_content]
  );

  if (!draft) return null;

  const onChange = (partial) => {
    dispatch(whatsappTemplateDraftChange(partial));
  };

  const onSampleChange = (key, value) => {
    onChange({
      sample_values: {
        ...(draft.sample_values || {}),
        [key]: value,
      },
    });
  };

  const handleSave = () => {
    const clientErrors = validateTemplateDraft(draft);
    if (clientErrors) {
      dispatch(
        saveWhatsappTemplateFail("Please fix validation errors", clientErrors)
      );
      toast.error("Please fix validation errors");
      return;
    }

    const payload = buildSavePayload(draft);
    dispatch(saveWhatsappTemplateRequest(draft.name, payload));
  };

  const handleDiscard = () => {
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    dispatch(whatsappTemplateResetDraft());
  };

  const handleBack = () => {
    if (dirty && !window.confirm("You have unsaved changes. Leave anyway?")) {
      return;
    }
    onBack?.();
  };

  return (
    <Box px={{ base: 4, md: 8 }} py={6} w="100%">
      <Flex
        align={{ base: "flex-start", md: "center" }}
        justify="space-between"
        gap={4}
        mb={6}
        flexDir={{ base: "column", md: "row" }}
      >
        <Box>
          <Text
            fontSize={{ base: "28px", md: "40px" }}
            fontWeight="800"
            color={C.primary}
            fontFamily="'Amiri', 'Tajawal', sans-serif"
            lineHeight="1.1"
          >
            Edit template
          </Text>
          <Text mt={2} color="var(--text-muted)" fontSize="sm">
            {draft.name}
            {dirty ? " · Unsaved changes" : ""}
          </Text>
        </Box>

        <Flex gap={2} flexWrap="wrap">
          <Button variant="ghost" onClick={handleBack}>
            Back
          </Button>
          <Button
            variant="outline"
            borderColor={C.border}
            onClick={handleDiscard}
            isDisabled={!dirty || saving}
          >
            Discard
          </Button>
          <Button
            bg={C.primary}
            color={brandPalette.cream}
            _hover={{ bg: C.secondary }}
            onClick={handleSave}
            isLoading={saving}
            isDisabled={!dirty}
          >
            Save
          </Button>
        </Flex>
      </Flex>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} alignItems="start">
        <VStack
          align="stretch"
          spacing={4}
          bg="var(--surface-raised)"
          border="1px solid"
          borderColor={C.border}
          borderRadius="16px"
          p={5}
        >
          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input value={draft.name || ""} isDisabled />
          </FormControl>

          <FormControl isInvalid={!!fieldError(validationErrors, "display_name")}>
            <FormLabel>Display name</FormLabel>
            <Input
              value={draft.display_name || ""}
              onChange={(e) => onChange({ display_name: e.target.value })}
            />
            <FormErrorMessage>
              {fieldError(validationErrors, "display_name")}
            </FormErrorMessage>
          </FormControl>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isInvalid={!!fieldError(validationErrors, "language")}>
              <FormLabel>Language</FormLabel>
              <Input
                value={draft.language || ""}
                onChange={(e) => onChange({ language: e.target.value })}
              />
              <FormErrorMessage>
                {fieldError(validationErrors, "language")}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!fieldError(validationErrors, "category")}>
              <FormLabel>Category</FormLabel>
              <Input
                value={draft.category || ""}
                onChange={(e) => onChange({ category: e.target.value })}
              />
              <FormErrorMessage>
                {fieldError(validationErrors, "category")}
              </FormErrorMessage>
            </FormControl>
          </SimpleGrid>

          <FormControl isInvalid={!!fieldError(validationErrors, "header_type")}>
            <FormLabel>Header type</FormLabel>
            <Select
              value={draft.header_type || "NONE"}
              onChange={(e) => onChange({ header_type: e.target.value })}
            >
              <option value="NONE">NONE</option>
              <option value="TEXT">TEXT</option>
              <option value="IMAGE">IMAGE</option>
              <option value="VIDEO">VIDEO</option>
              <option value="DOCUMENT">DOCUMENT</option>
            </Select>
            <FormErrorMessage>
              {fieldError(validationErrors, "header_type")}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!fieldError(validationErrors, "body_content")}>
            <FormLabel>Body content</FormLabel>
            <Textarea
              value={draft.body_content || ""}
              onChange={(e) => onChange({ body_content: e.target.value })}
              minH="220px"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
              fontSize="sm"
            />
            <FormErrorMessage>
              {fieldError(validationErrors, "body_content")}
            </FormErrorMessage>
          </FormControl>

          <Box>
            <Text fontWeight="700" mb={2} fontSize="sm">
              Detected variables
            </Text>
            <VariableChips variables={placeholders} />
          </Box>

          {placeholders.length > 0 && (
            <Box>
              <Text fontWeight="700" mb={3} fontSize="sm">
                Sample values
              </Text>
              <VStack align="stretch" spacing={3}>
                {placeholders.map((key) => (
                  <FormControl key={key}>
                    <FormLabel fontSize="sm">{`{{${key}}}`}</FormLabel>
                    <Input
                      value={draft.sample_values?.[key] ?? ""}
                      onChange={(e) => onSampleChange(key, e.target.value)}
                      placeholder={`Sample for ${key}`}
                    />
                  </FormControl>
                ))}
              </VStack>
            </Box>
          )}
        </VStack>

        <Box
          bg="var(--surface-raised)"
          border="1px solid"
          borderColor={C.border}
          borderRadius="16px"
          p={5}
          position={{ lg: "sticky" }}
          top={{ lg: "24px" }}
        >
          <Text fontWeight="700" mb={3}>
            Live preview
          </Text>
          <TemplatePreviewBubble
            bodyContent={draft.body_content}
            sampleValues={draft.sample_values}
            displayName={draft.display_name || draft.name}
          />
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default EditTemplateForm;
